import React from 'react';
import { Tag, Tooltip } from 'antd';
import { TruckTire } from '../services/truckTireService';
import truckTireService from '../services/truckTireService';
import { WhatsAppOutlined } from '@ant-design/icons';
import { getTruckTireInquiryWhatsAppUrl } from '../utils/productInquiry';
import { PRODUCT_IMAGE_PLACEHOLDER } from '../utils/productImage';

interface TruckTireCardProps {
    truckTire: TruckTire;
    onClick?: (truckTire: TruckTire) => void;
}

const TruckTireCard: React.FC<TruckTireCardProps> = ({ truckTire, onClick }) => {

    const handleClick = () => {
        if (onClick) {
            onClick(truckTire);
        }
    };

    const getMainImage = () => {
        // Kamyon lastikleri için özel resim havuzundan seç
        return truckTireService.formatImageUrl(truckTire.UrunResim, truckTire);
    };

    const isInStock = truckTire.StokMiktari > 0;
    const hasDiscount = truckTire.HavaleIndirimi > 0;
    const inquiryUrl = getTruckTireInquiryWhatsAppUrl(
        truckTire,
        `https://www.sakaryalastik.com/kamyon-lastikleri/${truckTire.UrunID}`
    );

    return (
        <div
            onClick={handleClick}
            className='w-full sm:w-[49%] md:w-[49%] lg:w-[32%] xl:w-[23%] 2xl:w-[18%] min-w-52 border
       rounded-md bg-white hover:shadow-lg transition-shadow duration-300 pb-4 flex flex-col gap-4 cursor-pointer'
            itemScope
            itemType="https://schema.org/Product"
        >
            {/* Ürün Resmi */}
            <div className='w-full flex justify-center relative'>
                <img
                    src={getMainImage()}
                    alt={`${truckTire.Markasi} ${truckTire.MalzemeAdi} - Kamyon Lastiği`}
                    title={`${truckTire.Markasi} Kamyon Lastiği`}
                    className='w-full h-40 object-cover rounded-t-lg'
                    itemProp="image"
                    onError={(e) => {
                        if (!e.currentTarget.src.endsWith(PRODUCT_IMAGE_PLACEHOLDER)) {
                            e.currentTarget.src = PRODUCT_IMAGE_PLACEHOLDER;
                        }
                    }}
                />

                {/* Kategori Etiketi */}
                <div className='absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded'>
                    {truckTire.Grup}
                </div>

                {/* İndirim Etiketi */}
                {!truckTire.hidePrices && hasDiscount && (
                    <div className='absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded'>
                        -%{truckTire.HavaleIndirimi}
                    </div>
                )}

            </div>

            {/* Hidden Schema.org meta data */}
            <meta itemProp="name" content={truckTire.MalzemeAdi} />
            <meta itemProp="brand" content={truckTire.Markasi} />
            <meta itemProp="sku" content={truckTire.MalzemeKodu} />
            <meta itemProp="description" content={`${truckTire.Markasi} ${truckTire.MalzemeAdi} kamyon lastiği. ${truckTire.Grup} kategorisinde.`} />

            <div className='w-full flex flex-col items-center gap-4 overflow-hidden px-3'>
                {/* Marka ve Ürün Adı */}
                <div className='flex flex-col items-center gap-2 text-center'>
                    <div className='text-blue-600 font-semibold text-sm'>{truckTire.Markasi}</div>
                    <Tooltip title={truckTire.MalzemeAdi}>
                        <div className='font-bold text-lg text-gray-800 overflow-hidden text-ellipsis w-[98%] line-clamp-2'>
                            {truckTire.MalzemeAdi}
                        </div>
                    </Tooltip>
                </div>

                {/* Kategori */}
                <div className='w-full flex justify-center'>
                    <Tag color="blue" className='text-xs'>
                        {truckTire.Grup}
                    </Tag>
                </div>

                {/* Fiyat Bilgileri */}
                {truckTire.hidePrices ? (
                    <a
                        href={inquiryUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2 text-center text-sm font-semibold text-white hover:bg-green-700"
                    >
                        <WhatsAppOutlined />
                        Lütfen Fiyatlar İçin Bize Ulaşın
                    </a>
                ) : (
                    <div className='w-full flex flex-col items-center gap-2'>
                        {hasDiscount && truckTire.ListeFiyati > truckTire.HavaleFiyati && (
                            <div className='text-sm text-gray-500 line-through'>
                                {truckTireService.formatPrice(truckTire.ListeFiyati)}
                            </div>
                        )}

                        <div className='text-2xl font-bold text-green-600' itemProp="price">
                            {truckTireService.formatPrice(truckTire.HavaleFiyati)}
                        </div>

                        <div className='text-xs text-gray-500'>KDV Dahil Havale Fiyatı</div>
                    </div>
                )}

                {/* Stok ve Ürün Kodu */}
                <div className='w-full flex flex-col gap-1 text-xs text-gray-600'>
                    {isInStock && (
                        <div className='flex justify-between items-center'>
                            <span>Stok:</span>
                            <span className='text-green-600 font-semibold'>
                                {truckTire.StokMiktari} Adet
                            </span>
                        </div>
                    )}
                    <div className='flex justify-between items-center'>
                        <span>Kod:</span>
                        <span className='font-mono'>{truckTire.MalzemeKodu}</span>
                    </div>
                </div>

                {/* CTA Button */}
                <div className='w-full mt-2'>
                    <button
                        className={`w-full py-2 px-4 rounded text-sm font-medium transition-colors duration-200 ${isInStock
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        disabled={!isInStock}
                    >
                            Detayları Görüntüle
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TruckTireCard;
